import { normalizeToStringId } from '@helper';
import { Channel, MultiLocaleFormData } from '@model';
import { deepClone } from '../helper/deep-clone.helper';
import { ExternalPointDto, ExternalPointFormModel, Interval } from '../model/external-point.model';

function buildReportingIntervalIndex(reportingIntervals: Interval[]): Map<number, string> {
  const intervalDurationToIdMap = new Map<number, string>();
  for (const interval of reportingIntervals) {
    const durationInMs = Number(interval.durationInMilliseconds);
    if (!Number.isNaN(durationInMs)) {
      intervalDurationToIdMap.set(durationInMs, normalizeToStringId(interval.id));
    }
  }
  return intervalDurationToIdMap;
}

function mapReportingIntervalToFormId(
  reportingIntervalMs: number | string | null | undefined,
  index: Map<number, string>,
): string {
  const ms = Number(reportingIntervalMs);
  if (Number.isNaN(ms)) return '';
  return index.get(ms) ?? '';
}

function getDefaultChannelId(channels: Channel[], point: Pick<ExternalPointDto, 'id' | 'channelId'>): string {
  const isNew = point?.id == null;
  if (isNew) {
    return channels && channels.length > 0 ? normalizeToStringId(channels[0].id as any) : '-1';
  }
  const channelId = normalizeToStringId(point?.channelId ?? '');
  return channelId !== '' ? channelId : '-1';
}

function getDefaultChannelDisplayLabel(channels: Channel[]): string {
  const first = channels && channels.length > 0 ? channels[0] : null;
  return first && (first as any).displayLabel ? String((first as any).displayLabel) : '-1';
}

function appendSuffixToLabels(labels: Record<string, string>, channelSuffixName: string): Record<string, string> {
  const safe = deepClone(labels ?? {});
  return Object.fromEntries(
    Object.entries(safe).map(([locale, text]) => [locale, `${String(text ?? '').trim()}${channelSuffixName}`]),
  );
}

function computeDisplayLabels(
  point: Pick<ExternalPointDto, 'id' | 'channelId'>,
  existingDisplayLabels: Record<string, string> | null | undefined,
  sdpDisplayLabelsByLocale: MultiLocaleFormData | null | undefined,
  channels: Channel[],
): Record<string, string> {
  const isNew = point?.id == null;

  if (isNew) {
    const base = (deepClone(sdpDisplayLabelsByLocale) as Record<string, string>) ?? {};
    if (Object.keys(base).length === 0) return {};
    const channelSuffix = ` - ${getDefaultChannelDisplayLabel(channels)}`;
    return appendSuffixToLabels(base, channelSuffix);
  }

  const sdp = (sdpDisplayLabelsByLocale as Record<string, string>) ?? {};
  const ext = existingDisplayLabels ?? {};
  return { ...sdp, ...ext };
}

export function mapPointToFormMapper(
  point: ExternalPointDto,
  reportingIntervals: Interval[],
  sdpDisplayLabelsByLocale: MultiLocaleFormData,
  channels: Channel[],
): ExternalPointFormModel {
  const intervalIndex = buildReportingIntervalIndex(reportingIntervals);
  const newPoint = deepClone(point);
  return {
    channelId: getDefaultChannelId(channels, newPoint),
    reportingInterval: mapReportingIntervalToFormId(point.reportingIntervalMs, intervalIndex),
    sourceId: normalizeToStringId(newPoint.alternateIds?.sourceId ?? newPoint.sourceId ?? ''),
    displayLabels: computeDisplayLabels(newPoint, newPoint.displayLabels, sdpDisplayLabelsByLocale, channels),
    dataproviderId: newPoint.dataproviderId ?? '',
    expressionFactor: newPoint.expressionFactor ?? 1,
    veeBypass: newPoint.veeBypass ?? true,
  };
}
